import { useFormik } from "formik";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  InvisibleInput,
  Tabs,
  useDiscardModal,
  useModals,
} from "senf-atomic-design-system";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import Dropdown from "senf-atomic-design-system/src/components/core/dropdown/Dropdown";
import { Combobox } from "../../../../core/inputs/Combobox";
import CesiumControls from "./CesiumControls";

const SettingsModal = ({
  handleDelete,
  mapStyles,
  formik: initial,
}: {
  handleDelete: () => void;
  mapStyles: any;
  formik: any;
}) => {
  const { t } = useTranslation();
  const { displayDiscardModal } = useDiscardModal();
  const { closeModal } = useModals();

  const formik = useFormik({ initialValues: initial?.values });
  useEffect(() => {
    initial?.setValues(formik?.values);
  }, [formik]);

  return (
    <div className="flex flex-col gap-5 px-5 pb-5">
      <Combobox
        enableSearch={false}
        size="lg"
        value={
          mapStyles.find(({ value }) => value === formik.values?.mapStyle)
            ?.label || "Default Map style"
        }
        width="max"
        options={mapStyles}
        onChange={(option) => {
          formik.setFieldValue("mapStyle", option.value);
        }}
      />

      {formik.values.mapStyle === "cesium" && (
        <CesiumControls
          bimModels={formik.values.bimModels}
          onBimModelsChange={(models) =>
            formik.setFieldValue("bimModels", models)
          }
          imageryStyle={formik.values.imageryStyle || "mapbox-outdoors"}
          onImageryStyleChange={(style) => {
            formik.setFieldValue("imageryStyle", style);
          }}
          osmBuildingsEnabled={formik.values.osmBuildingsEnabled}
          onOsmBuildingsChange={(enabled) =>
            formik.setFieldValue("osmBuildingsEnabled", enabled)
          }
          osmBuildingColor={formik.values.osmBuildingColor}
          onOsmBuildingColorChange={(color, opacity) => {
            formik.setFieldValue("osmBuildingColor", color);
            formik.setFieldValue("osmBuildingOpacity", opacity);
          }}
          osmBuildingOpacity={formik.values.osmBuildingOpacity}
          geoidHeightDifference={formik.values.geoidHeightDifference}
          onGeoidHeightDifferenceChange={(height) => {
            formik.setFieldValue("geoidHeightDifference", height);
          }}
        />
      )}

      <div className=" text-text-critical -mx-5 flex items-center justify-between border-t px-5 pt-5">
        <h4 className="body-md">
          {t("util:delete", { val: t("global:map") })}
        </h4>
        <Button
          variant="shapeless"
          critical
          leadingIcon="trash"
          onClick={() =>
            displayDiscardModal({
              title: t("util:delete", { val: t("global:map") }),
              description: t("global:deleteDescription"),
              submitText: t("global:delete"),
              onSubmit: async () => {
                handleDelete();
              },
            })
          }
        />
      </div>
    </div>
  );
};

const Header = ({
  title,
  setTitle,
  handleDelete,
  index,
  setIndex,
  formik,
  mapStyles,
}) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { displayDiscardModal } = useDiscardModal();

  return (
    <div className="border-border-subdued z-50 flex h-auto flex-col border-b bg-white p-5 pb-0">
      <div className="flex w-full items-center  justify-between gap-3">
        <h2 className="heading-lg w-full pl-3">
          <InvisibleInput
            size="body-lg-bold"
            value={title}
            onChange={setTitle}
            maxRows={1}
            placeholder={t("global:title")}
          />
        </h2>

        <Button
          leadingIcon="settings-fill"
          size="md"
          variant="shapeless"
          key="settings"
          tooltipLabel={t("global:settings")}
          textSubdued
          onClick={() => {
            openModal(
              <SettingsModal
                handleDelete={handleDelete}
                mapStyles={mapStyles}
                formik={formik}
              />,
              {
                title: t("global:settings"),
                id: "settingsModal",
                zIndex: 9999999999,
              },
            );
          }}
        />
        <Button
          size="md"
          onClick={() =>
            displayDiscardModal({
              // title: t("dialogMap:add.discardTitle"),
              // description: t("dialogMap:add.discardDescription"),
              // submitText: t("global:discard"),
              onSubmit: async () => {},
            })
          }
          leadingIcon="x-md"
          tooltipLabel={t("global:close")}
        />
      </div>
      <Tabs
        active={index}
        classNames={{ wrapper: "ml-2 h-14" }}
        setActive={setIndex}
        // type="filled"
        tabs={[
          {
            tab: "planningArea",
            text: t("global:planningArea"),
            icon: "area",
          },
          {
            tab: "upload",
            text: t("util:upload", { val: t("global:data") }),
            icon: "share-alt",
            disabled: !formik.values.mapbounds,
          },
          {
            tab: "create",
            text: t("util:insert", { val: t("global:data") }),
            icon: "plus-md",
            disabled: !formik.values.mapbounds,
          },
        ].filter(Boolean)}
      />
    </div>
  );
};

export default Header;
